import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from '../../api/auth';
import logo from '../../res/MayorX.svg';
import { Organization } from '../../models';
import { useBranding } from '../../contexts/BrandingContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  List as FiList,
  Power as FiPower,
  List as FiMenu,
  X as FiX,
  ChevronDown as FiChevronDown,
  ChevronRight as FiChevronRight,
} from 'react-bootstrap-icons';
import {
  getAdminNavItems,
  isNavGroup,
  NAV_GROUP_PATH_PREFIXES,
} from './adminNavItems';
import { MOBILE_NAV_ICONS } from './mobileNavIcons';
import {
  Navbar,
  Nav,
  Container,
  Offcanvas,
  Form,
  Button,
  Badge,
} from 'react-bootstrap';
import ExchangeRatePopover from './ExchangeRatePopover';
import UserMenu from './UserMenu';

interface MobileNavProps {
  organization: Organization | null;
  isAdmin?: boolean;
  panelPermissions?: string[] | null;
  isGodMode?: boolean;
  isMenuOpen?: boolean;
  onToggleMenu?: () => void;
}

const MobileNav: React.FC<MobileNavProps> = ({
  organization,
  isAdmin = false,
  panelPermissions = null,
  isGodMode = false,
  isMenuOpen = false,
  onToggleMenu,
}) => {
  const { branding } = useBranding();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [groupMenuOpen, setGroupMenuOpen] = useState<Record<string, boolean>>({
    orders: true,
    finance: true,
    purchasing: true,
  });

  useEffect(() => {
    setGroupMenuOpen(prev => {
      const next = { ...prev };
      Object.entries(NAV_GROUP_PATH_PREFIXES).forEach(
        ([groupKey, prefixes]) => {
          if (prefixes.some(prefix => location.pathname.startsWith(prefix))) {
            next[groupKey] = true;
          }
        }
      );
      return next;
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const navItems = useMemo(() => {
    return getAdminNavItems(isAdmin, panelPermissions, isGodMode).map(item => {
      if (isNavGroup(item)) {
        return {
          groupKey: item.groupKey,
          icon: MOBILE_NAV_ICONS[item.icon] || FiList,
          text: item.text,
          path: null as null,
          children: item.children.map(child => ({
            icon: MOBILE_NAV_ICONS[child.icon] || FiList,
            text: child.text,
            path: child.path,
          })),
        };
      }
      return {
        icon: MOBILE_NAV_ICONS[item.icon] || FiList,
        text: item.text,
        path: item.path,
      };
    });
  }, [isAdmin, panelPermissions, isGodMode]);

  const handleNavClick = (path: string) => {
    navigate(path);
    if (onToggleMenu) {
      onToggleMenu();
    }
  };

  const handleBackdropClick = () => {
    if (onToggleMenu) {
      onToggleMenu();
    }
  };

  return (
    <>
      {/* Manual Backdrop Overlay */}
      {isMenuOpen && (
        <div
          className="position-fixed w-100 h-100"
          style={{
            top: 0,
            left: 0,
            zIndex: 1055,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            transition: 'opacity 0.3s ease',
          }}
          onClick={handleBackdropClick}
        />
      )}

      {/* Fixed Top Navigation */}
      <Navbar
        bg="body"
        expand={false}
        className="mobile-nav"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 1050,
          height: '70px',
          boxShadow: 'none',
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
        }}
      >
        <Container fluid className="px-3 min-w-0">
          <Navbar.Brand className="d-flex align-items-center min-w-0 flex-grow-1 me-1 overflow-hidden">
            {branding.logo ? (
              <img
                src={branding.logo}
                width="36"
                height="36"
                className="me-2"
                alt="Logo"
                style={{ borderRadius: '8px', objectFit: 'contain' }}
              />
            ) : (
              <img
                src={logo}
                width="44"
                height="36"
                alt="MayorX"
                style={{
                  borderRadius: 0,
                  objectFit: 'contain',
                  opacity: 0.88,
                }}
                data-testid="official-logo"
                className="me-2 official-logo"
              />
            )}
            {organization && (
              <span
                className="d-none d-sm-inline-flex align-items-center gap-2 min-w-0 flex-grow-1 overflow-hidden"
                style={{ fontSize: '14px', fontWeight: '500' }}
              >
                <span
                  className="text-truncate min-w-0"
                  title={organization.name}
                >
                  {organization.name}
                </span>
                {organization.isSandbox && (
                  <Badge
                    bg="secondary"
                    className="flex-shrink-0 fw-normal"
                    style={{ fontSize: '0.65rem', letterSpacing: '0.1em' }}
                  >
                    PRUEBAS
                  </Badge>
                )}
              </span>
            )}
          </Navbar.Brand>

          <div className="d-flex align-items-center gap-2 me-2 flex-shrink-0">
            <ExchangeRatePopover
              organizationId={organization?.id}
              compact
            />
            <UserMenu compact />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleTheme}
              aria-label="Cambiar tema"
            >
              {theme === 'dark' ? '☾' : '☀︎'}
            </Button>
          </div>

          <Navbar.Toggle
            aria-controls="mobile-navbar"
            className="border-0"
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'var(--bs-body-color)',
            }}
            onClick={onToggleMenu}
          >
            {isMenuOpen ? (
              <FiX size={24} color="currentColor" />
            ) : (
              <FiMenu size={24} color="currentColor" />
            )}
          </Navbar.Toggle>
        </Container>
      </Navbar>

      {/* Offcanvas Menu */}
      <Offcanvas
        show={isMenuOpen}
        onHide={handleBackdropClick}
        placement="end"
        className="mobile-nav"
        style={{
          width: '280px',
          zIndex: 1060,
        }}
        backdrop={false}
        scroll={false}
        restoreFocus={false}
      >
        <Offcanvas.Header
          closeButton
          onHide={handleBackdropClick}
          className="border-bottom pb-3"
          style={{
            backgroundColor: 'var(--bs-tertiary-bg)',
            borderBottomColor: 'var(--bs-border-color)',
          }}
        >
          <Offcanvas.Title className="fw-bold">Menú</Offcanvas.Title>
          <div className="ms-auto me-2">
            <Form.Check
              type="switch"
              id="mobile-theme-switch"
              label="Oscuro"
              checked={theme === 'dark'}
              onChange={toggleTheme}
            />
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          <Nav className="flex-column">
            {navItems.map((item, index) => {
              if (
                'children' in item &&
                item.children &&
                item.children.length > 0
              ) {
                const groupKey = item.groupKey;
                const isGroupOpen = groupMenuOpen[groupKey] ?? true;
                const hasActiveChild = item.children.some(
                  child =>
                    location.pathname === child.path ||
                    location.pathname.startsWith(`${child.path}/`)
                );
                return (
                  <React.Fragment key={groupKey}>
                    <Nav.Item>
                      <Nav.Link
                        onClick={() =>
                          setGroupMenuOpen(prev => ({
                            ...prev,
                            [groupKey]: !(prev[groupKey] ?? true),
                          }))
                        }
                        className={`mobile-nav-link d-flex align-items-center justify-content-between px-3 py-3 border-0 ${
                          hasActiveChild ? 'bg-primary on-primary' : 'text-body'
                        }`}
                        style={{
                          fontSize: '16px',
                          fontWeight: hasActiveChild ? '600' : '500',
                          transition: 'all 0.2s ease',
                          borderRadius: '0',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <item.icon
                            className={`me-3 ${
                              hasActiveChild ? '' : 'mobile-nav-icon'
                            }`}
                            size={20}
                            style={{
                              color: hasActiveChild
                                ? 'var(--bs-primary-contrast)'
                                : 'var(--bs-secondary-color)',
                              transition: 'color 0.2s ease',
                            }}
                          />
                          <span>{item.text}</span>
                        </div>
                        {isGroupOpen ? (
                          <FiChevronDown
                            size={16}
                            style={{
                              color: hasActiveChild
                                ? 'var(--bs-primary-contrast)'
                                : 'var(--bs-secondary-color)',
                            }}
                          />
                        ) : (
                          <FiChevronRight
                            size={16}
                            style={{
                              color: hasActiveChild
                                ? 'var(--bs-primary-contrast)'
                                : 'var(--bs-secondary-color)',
                            }}
                          />
                        )}
                      </Nav.Link>
                    </Nav.Item>
                    {isGroupOpen && (
                      <>
                        {item.children.map((child, childIndex) => {
                          const isActive =
                            location.pathname === child.path ||
                            location.pathname.startsWith(`${child.path}/`);
                          return (
                            <Nav.Item key={`${index}-${childIndex}`}>
                              <Nav.Link
                                as="button"
                                type="button"
                                onClick={() => handleNavClick(child.path)}
                                className="mobile-nav-sublink d-flex align-items-center px-5 py-2 border-0 w-100 text-start"
                                style={{
                                  fontSize: '14px',
                                  fontWeight: isActive ? '600' : '400',
                                  transition: 'all 0.2s ease',
                                  borderRadius: '0',
                                  color: isActive
                                    ? 'var(--bs-body-color)'
                                    : 'var(--bs-secondary-color)',
                                  backgroundColor: 'var(--bs-tertiary-bg)',
                                }}
                              >
                                <child.icon
                                  className="me-3 mobile-nav-icon"
                                  size={15}
                                  style={{
                                    color: isActive
                                      ? 'var(--bs-body-color)'
                                      : 'var(--bs-secondary-color)',
                                    opacity: isActive ? 0.85 : 0.65,
                                    transition:
                                      'color 0.2s ease, opacity 0.2s ease',
                                  }}
                                />
                                <span>{child.text}</span>
                              </Nav.Link>
                            </Nav.Item>
                          );
                        })}
                      </>
                    )}
                  </React.Fragment>
                );
              }

              // Regular menu item (no children)
              const isActive = item.path && location.pathname === item.path;
              return (
                <Nav.Item key={index}>
                  <Nav.Link
                    onClick={() => item.path && handleNavClick(item.path)}
                    className={`mobile-nav-link d-flex align-items-center px-3 py-3 border-0 ${
                      isActive ? 'bg-primary on-primary' : 'text-body'
                    }`}
                    style={{
                      fontSize: '16px',
                      fontWeight: isActive ? '600' : '500',
                      transition: 'all 0.2s ease',
                      borderRadius: '0',
                    }}
                  >
                    <item.icon
                      className={`me-3 ${isActive ? '' : 'mobile-nav-icon'}`}
                      size={20}
                      style={{
                        color: isActive
                          ? 'var(--bs-primary-contrast)'
                          : 'var(--bs-secondary-color)',
                        transition: 'color 0.2s ease',
                      }}
                    />
                    <span>{item.text}</span>
                  </Nav.Link>
                </Nav.Item>
              );
            })}

            {/* Logout Button */}
            <Nav.Item className="mt-auto">
              <Nav.Link
                onClick={handleLogout}
                className="d-flex align-items-center px-3 py-3 border-0 text-danger"
                style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  borderRadius: '0',
                }}
              >
                <FiPower
                  className="me-3"
                  size={20}
                  style={{ color: 'currentColor' }}
                />
                <span>Cerrar Sesión</span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default MobileNav;
